// ============================================
// FINANCE - GESTION DES CLÔTURES DE CAISSE
// Liste avec validation hiérarchique
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
    Wallet,
    CheckCircle,
    XCircle,
    Clock,
    AlertTriangle,
    Eye,
    Filter,
    RefreshCw,
    Calendar,
    User,
    CreditCard,
    RotateCcw,
    Shield,
    TrendingUp,
    TrendingDown,
    Minus,
    Archive
} from 'lucide-react';

// ============================================
// STATUS BADGE COMPONENT
// ============================================
const StatusBadge = ({ status }) => {
    // Tous les statuts en attente affichent le même badge "En attente"
    const PENDING_STATUSES = ['pending', 'auto_closed_unverified', 'midnight_auto_closed', 'unverified'];
    
    const config = {
        pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock },
        recount_requested: { label: 'Recomptage demandé', color: 'bg-orange-100 text-orange-800 border-orange-300', icon: RotateCcw },
        validated: { label: 'Validée', color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle },
        rejected: { label: 'Refusée', color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle },
        archived: { label: 'Archivée', color: 'bg-gray-100 text-gray-800 border-gray-300', icon: Archive }
    };
    
    // Normaliser les statuts "auto" vers "pending"
    const normalizedStatus = PENDING_STATUSES.includes(status) ? 'pending' : status;
    const { label, color, icon: Icon } = config[normalizedStatus] || config.pending;
    
    return (
        <Badge className={`${color} border flex items-center gap-1`}>
            <Icon className="w-3 h-3" />
            {label}
        </Badge>
    );
};

// ============================================
// VARIANCE DISPLAY
// ============================================
const VarianceDisplay = ({ variance }) => {
    const absVariance = Math.abs(variance);
    const formatted = absVariance.toLocaleString('fr-FR');
    
    if (variance === 0) {
        return (
            <span className="flex items-center gap-1 text-emerald-600 font-semibold">
                <Minus className="w-4 h-4" />
                0 F
            </span>
        );
    }
    
    if (variance > 0) {
        return (
            <span className="flex items-center gap-1 text-emerald-600 font-semibold">
                <TrendingUp className="w-4 h-4" />
                +{formatted} F
            </span>
        );
    }
    
    return (
        <span className="flex items-center gap-1 text-red-600 font-semibold">
            <TrendingDown className="w-4 h-4" />
            -{formatted} F
        </span>
    );
};

// ============================================
// CLOSURE ROW COMPONENT
// ============================================
const ClosureRow = ({ closure, onValidate, onReject, onViewDetail, onRequestRecount, onForceClose, userRole }) => {
    // Statuts en attente (toutes variantes)
    const PENDING_STATUSES = ['pending', 'recount_requested', 'auto_closed_unverified', 'midnight_auto_closed', 'unverified'];
    const TREATED_STATUSES = ['validated', 'rejected', 'archived'];
    
    const isPending = closure.is_pending || PENDING_STATUSES.includes(closure.status);
    const isTreated = closure.is_treated || TREATED_STATUSES.includes(closure.status);
    const isAdmin = userRole === 'ADMIN';
    
    // Afficher le statut normalisé
    const getDisplayStatus = () => {
        if (isPending) return 'En attente';
        if (closure.status === 'validated') return 'Validée';
        if (closure.status === 'rejected') return 'Rejetée';
        if (closure.status === 'archived') return 'Archivée';
        return closure.status;
    };
    
    return (
        <div className={`p-4 rounded-lg border-2 ${
            isPending 
                ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700' 
                : isTreated && closure.status === 'validated'
                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-700'
                    : isTreated && closure.status === 'rejected'
                        ? 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-700'
                        : 'bg-white dark:bg-zinc-900 border-gray-200 dark:border-zinc-700'
        }`}>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Info principale */}
                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Caisse + User */}
                    <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Caisse</p>
                        <p className="font-bold text-gray-900 dark:text-white text-lg">{closure.register_name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                            {closure.user_name} <span className="text-xs text-gray-500">({closure.user_role})</span>
                        </p>
                    </div>
                    
                    {/* Date */}
                    <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date / Heure</p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                            {new Date(closure.created_at).toLocaleDateString('fr-FR')}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            {new Date(closure.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                    </div>
                    
                    {/* Montants - Amélioré pour lisibilité */}
                    <div className="col-span-2 md:col-span-1">
                        <div className="grid grid-cols-3 gap-3 p-2 bg-white/50 dark:bg-black/20 rounded-lg">
                            <div className="text-center">
                                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase">Théorique</p>
                                <p className="font-bold text-gray-900 dark:text-white">{closure.theoretical_total?.toLocaleString('fr-FR')} F</p>
                            </div>
                            <div className="text-center border-x border-gray-200 dark:border-gray-700">
                                <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase">Compté</p>
                                <p className="font-bold text-gray-900 dark:text-white">{closure.actual_total?.toLocaleString('fr-FR')} F</p>
                            </div>
                            <div className="text-center">
                                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Écart</p>
                                <VarianceDisplay variance={closure.variance} />
                            </div>
                        </div>
                    </div>
                    
                    {/* Statut + Qui doit valider */}
                    <div>
                        <StatusBadge status={closure.status} />
                        {isPending && (
                            <p className="text-xs font-semibold text-orange-700 dark:text-orange-400 mt-2 flex items-center gap-1 bg-orange-100 dark:bg-orange-900/30 px-2 py-1 rounded">
                                <Shield className="w-3 h-3" />
                                {closure.required_validator}
                            </p>
                        )}
                        {closure.validated_by_name && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-medium">
                                ✓ Par {closure.validated_by_name}
                            </p>
                        )}
                    </div>
                </div>
                
                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onViewDetail(closure)}
                        className="bg-white dark:bg-zinc-800"
                    >
                        <Eye className="w-4 h-4 mr-1" />
                        Détail
                    </Button>
                    
                    {isPending && closure.can_validate && (
                        <>
                            <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                                onClick={() => onValidate(closure)}
                            >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Valider
                            </Button>
                            <Button
                                variant="destructive"
                                size="sm"
                                className="font-semibold"
                                onClick={() => onReject(closure)}
                            >
                                <XCircle className="w-4 h-4 mr-1" />
                                Refuser
                            </Button>
                            {closure.status !== 'recount_requested' && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="bg-white dark:bg-zinc-800"
                                    onClick={() => onRequestRecount(closure)}
                                >
                                    <RotateCcw className="w-4 h-4 mr-1" />
                                    Recompter
                                </Button>
                            )}
                        </>
                    )}
                    
                    {/* Bouton Forcer Clôture - Admin uniquement */}
                    {isPending && isAdmin && (
                        <Button
                            size="sm"
                            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                            onClick={() => onForceClose(closure)}
                        >
                            <Shield className="w-4 h-4 mr-1" />
                            Forcer
                        </Button>
                    )}
                    
                    {isPending && !closure.can_validate && !isAdmin && (
                        <Badge variant="outline" className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800">
                            Non autorisé
                        </Badge>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================
// MAIN PAGE COMPONENT
// ============================================
const CashClosuresPage = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [closures, setClosures] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [total, setTotal] = useState(0);
    
    // Filters
    const [statusFilter, setStatusFilter] = useState('all');
    const [registerFilter, setRegisterFilter] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    
    // Filter options
    const [registers, setRegisters] = useState([]);
    const [users, setUsers] = useState([]);
    
    // Modals
    const [selectedClosure, setSelectedClosure] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showValidateModal, setShowValidateModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showRecountModal, setShowRecountModal] = useState(false);
    const [showForceCloseModal, setShowForceCloseModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [recountReason, setRecountReason] = useState('');
    const [forceCloseReason, setForceCloseReason] = useState('');
    const [processing, setProcessing] = useState(false);
    
    // ============================================
    // FETCH DATA
    // ============================================
    const fetchClosures = useCallback(async () => {
        try {
            let url = '/finance/closures/list?limit=100';
            
            if (statusFilter !== 'all') {
                url += `&status=${statusFilter}`;
            }
            if (registerFilter !== 'all') {
                url += `&register_id=${registerFilter}`;
            }
            if (dateFrom) {
                url += `&date_from=${dateFrom}T00:00:00Z`;
            }
            if (dateTo) {
                url += `&date_to=${dateTo}T23:59:59Z`;
            }
            
            const response = await api().get(url);
            // Handle both direct data and response.data patterns
            const data = response.data || response;
            console.log('[CashClosures] Fetched:', data);
            
            setClosures(data.closures || []);
            setTotal(data.total || 0);
            setPendingCount(data.pending_count || 0);
        } catch (error) {
            console.error('Error fetching closures:', error);
            toast.error('Erreur de chargement des clôtures');
        } finally {
            setLoading(false);
        }
    }, [api, statusFilter, registerFilter, dateFrom, dateTo]);
    
    const fetchFilters = useCallback(async () => {
        try {
            const [regsData, usersData] = await Promise.all([
                api().get('/finance/closures/registers/list').catch(() => []),
                api().get('/finance/closures/users/list').catch(() => [])
            ]);
            // Ensure we always have arrays
            setRegisters(Array.isArray(regsData) ? regsData : []);
            setUsers(Array.isArray(usersData) ? usersData : []);
        } catch (error) {
            console.error('Error fetching filters:', error);
            setRegisters([]);
            setUsers([]);
        }
    }, [api]);
    
    useEffect(() => {
        fetchClosures();
        fetchFilters();
    }, [fetchClosures, fetchFilters]);
    
    // Auto-refresh every 30 seconds
    useEffect(() => {
        const interval = setInterval(fetchClosures, 30000);
        return () => clearInterval(interval);
    }, [fetchClosures]);
    
    // ============================================
    // ACTIONS
    // ============================================
    const handleValidate = async () => {
        if (!selectedClosure) return;
        setProcessing(true);
        
        try {
            await api().post(`/finance/closures/${selectedClosure.id}/validate`, {
                approved: true
            });
            toast.success('Clôture validée avec succès');
            setShowValidateModal(false);
            setSelectedClosure(null);
            fetchClosures();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
        } finally {
            setProcessing(false);
        }
    };
    
    const handleReject = async () => {
        if (!selectedClosure || !rejectReason.trim()) {
            toast.error('Une raison est obligatoire pour refuser');
            return;
        }
        setProcessing(true);
        
        try {
            await api().post(`/finance/closures/${selectedClosure.id}/validate`, {
                approved: false,
                reason: rejectReason
            });
            toast.success('Clôture refusée');
            setShowRejectModal(false);
            setSelectedClosure(null);
            setRejectReason('');
            fetchClosures();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du refus');
        } finally {
            setProcessing(false);
        }
    };
    
    const handleRequestRecount = async () => {
        if (!selectedClosure) return;
        setProcessing(true);
        
        try {
            await api().post(`/finance/closures/${selectedClosure.id}/validate`, {
                approved: false,
                request_recount: true,
                reason: recountReason || 'Recomptage demandé'
            });
            toast.success('Demande de recomptage envoyée');
            setShowRecountModal(false);
            setSelectedClosure(null);
            setRecountReason('');
            fetchClosures();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setProcessing(false);
        }
    };
    
    // Handler pour forcer la clôture (Admin uniquement)
    const handleForceClose = async () => {
        if (!selectedClosure) return;
        setProcessing(true);
        
        try {
            await api().post(`/finance/closures/${selectedClosure.id}/force-close`, {
                reason: forceCloseReason || 'Clôture forcée par administrateur'
            });
            toast.success('Clôture forcée avec succès');
            setShowForceCloseModal(false);
            setSelectedClosure(null);
            setForceCloseReason('');
            fetchClosures();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la clôture forcée');
        } finally {
            setProcessing(false);
        }
    };
    
    // ============================================
    // RENDER
    // ============================================
    return (
        <div className="space-y-6 pb-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                        <Wallet className="w-7 h-7 text-emerald-600" />
                        Clôtures de Caisse
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Validation hiérarchique des clôtures
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {pendingCount > 0 && (
                        <Badge className="bg-red-500 text-white text-lg px-3 py-1 animate-pulse">
                            {pendingCount} en attente
                        </Badge>
                    )}
                    <Button variant="outline" onClick={fetchClosures}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Actualiser
                    </Button>
                    <Button onClick={() => navigate('/cash-closing')}>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Nouvelle clôture
                    </Button>
                </div>
            </div>
            
            {/* Filters */}
            <Card className="border-0 shadow-md">
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-medium">Filtres:</span>
                        </div>
                        
                        {/* Status Filter */}
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                <SelectItem value="pending">🟡 En attente</SelectItem>
                                <SelectItem value="validated">🟢 Validées</SelectItem>
                                <SelectItem value="rejected">🔴 Refusées</SelectItem>
                            </SelectContent>
                        </Select>
                        
                        {/* Register Filter */}
                        <Select value={registerFilter} onValueChange={setRegisterFilter}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Caisse" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes les caisses</SelectItem>
                                {(registers || []).map(r => (
                                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        
                        {/* Date Range */}
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <Input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className="w-[140px]"
                                placeholder="Du"
                            />
                            <span className="text-gray-500">→</span>
                            <Input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                className="w-[140px]"
                                placeholder="Au"
                            />
                        </div>
                        
                        {/* Quick filter: Pending only */}
                        <Button
                            variant={statusFilter === 'pending' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
                            className={statusFilter === 'pending' ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
                        >
                            🔥 En attente uniquement
                        </Button>
                    </div>
                </CardContent>
            </Card>
            
            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-0 shadow-sm bg-yellow-50 dark:bg-yellow-950/20">
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-yellow-600">{pendingCount}</p>
                        <p className="text-sm text-yellow-700">En attente</p>
                    </CardContent>
                </Card>
                <Card className="border-0 shadow-sm bg-emerald-50 dark:bg-emerald-950/20">
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-emerald-600">
                            {closures.filter(c => c.status === 'validated').length}
                        </p>
                        <p className="text-sm text-emerald-700">Validées</p>
                    </CardContent>
                </Card>
                <Card className="border-0 shadow-sm bg-red-50 dark:bg-red-950/20">
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-red-600">
                            {closures.filter(c => c.status === 'rejected').length}
                        </p>
                        <p className="text-sm text-red-700">Refusées</p>
                    </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-gray-600">{total}</p>
                        <p className="text-sm text-gray-500">Total</p>
                    </CardContent>
                </Card>
            </div>
            
            {/* Closures List */}
            <Card className="border-0 shadow-md">
                <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                        Liste des clôtures
                        <Badge variant="outline">{closures.length}</Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : closures.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>Aucune clôture trouvée</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {closures.map(closure => (
                                <ClosureRow
                                    key={closure.id}
                                    closure={closure}
                                    userRole={user?.role}
                                    onValidate={(c) => { setSelectedClosure(c); setShowValidateModal(true); }}
                                    onReject={(c) => { setSelectedClosure(c); setShowRejectModal(true); }}
                                    onViewDetail={(c) => { setSelectedClosure(c); setShowDetailModal(true); }}
                                    onRequestRecount={(c) => { setSelectedClosure(c); setShowRecountModal(true); }}
                                    onForceClose={(c) => { setSelectedClosure(c); setShowForceCloseModal(true); }}
                                />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
            
            {/* ============================================ */}
            {/* MODALS */}
            {/* ============================================ */}
            
            {/* Detail Modal */}
            <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Eye className="w-5 h-5" />
                            Détail de la clôture
                        </DialogTitle>
                    </DialogHeader>
                    {selectedClosure && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-500">Caisse</p>
                                    <p className="font-semibold">{selectedClosure.register_name}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Utilisateur</p>
                                    <p className="font-semibold">{selectedClosure.user_name} ({selectedClosure.user_role})</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Date</p>
                                    <p className="font-semibold">
                                        {new Date(selectedClosure.created_at).toLocaleString('fr-FR')}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Statut</p>
                                    <StatusBadge status={selectedClosure.status} />
                                </div>
                            </div>
                            
                            <hr />
                            
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div className="p-4 bg-blue-50 rounded-lg">
                                    <p className="text-sm text-blue-600">Théorique</p>
                                    <p className="text-2xl font-bold text-blue-700">
                                        {selectedClosure.theoretical_total?.toLocaleString('fr-FR')} F
                                    </p>
                                </div>
                                <div className="p-4 bg-emerald-50 rounded-lg">
                                    <p className="text-sm text-emerald-600">Réel</p>
                                    <p className="text-2xl font-bold text-emerald-700">
                                        {selectedClosure.actual_total?.toLocaleString('fr-FR')} F
                                    </p>
                                </div>
                                <div className={`p-4 rounded-lg ${
                                    selectedClosure.variance === 0 ? 'bg-emerald-50' :
                                    selectedClosure.variance > 0 ? 'bg-emerald-50' : 'bg-red-50'
                                }`}>
                                    <p className="text-sm text-gray-600">Écart</p>
                                    <p className={`text-2xl font-bold ${
                                        selectedClosure.variance === 0 ? 'text-emerald-700' :
                                        selectedClosure.variance > 0 ? 'text-emerald-700' : 'text-red-700'
                                    }`}>
                                        {selectedClosure.variance > 0 ? '+' : ''}{selectedClosure.variance?.toLocaleString('fr-FR')} F
                                    </p>
                                </div>
                            </div>
                            
                            {selectedClosure.variance_reason && (
                                <div>
                                    <p className="text-sm text-gray-500">Raison de l'écart</p>
                                    <p className="p-2 bg-gray-50 rounded">{selectedClosure.variance_reason}</p>
                                </div>
                            )}
                            
                            {selectedClosure.validated_by_name && (
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <p className="text-sm text-gray-500">
                                        {selectedClosure.status === 'validated' ? 'Validée' : 'Refusée'} par
                                    </p>
                                    <p className="font-semibold">{selectedClosure.validated_by_name}</p>
                                    {selectedClosure.validated_at && (
                                        <p className="text-xs text-gray-500">
                                            {new Date(selectedClosure.validated_at).toLocaleString('fr-FR')}
                                        </p>
                                    )}
                                    {selectedClosure.validator_notes && (
                                        <p className="mt-2 text-sm italic">"{selectedClosure.validator_notes}"</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                            Fermer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Validate Confirmation Modal */}
            <Dialog open={showValidateModal} onOpenChange={setShowValidateModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-emerald-600">
                            <CheckCircle className="w-5 h-5" />
                            Confirmer la validation
                        </DialogTitle>
                        <DialogDescription>
                            Vous êtes sur le point de valider cette clôture de caisse.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedClosure && (
                        <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                            <p><strong>Caisse:</strong> {selectedClosure.register_name}</p>
                            <p><strong>Par:</strong> {selectedClosure.user_name}</p>
                            <p><strong>Écart:</strong> <VarianceDisplay variance={selectedClosure.variance} /></p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowValidateModal(false)} disabled={processing}>
                            Annuler
                        </Button>
                        <Button 
                            className="bg-emerald-600 hover:bg-emerald-700" 
                            onClick={handleValidate}
                            disabled={processing}
                        >
                            {processing ? 'Validation...' : 'Valider la clôture'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Reject Modal */}
            <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <XCircle className="w-5 h-5" />
                            Refuser la clôture
                        </DialogTitle>
                        <DialogDescription>
                            Une raison est obligatoire pour refuser cette clôture.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedClosure && (
                        <>
                            <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                                <p><strong>Caisse:</strong> {selectedClosure.register_name}</p>
                                <p><strong>Par:</strong> {selectedClosure.user_name}</p>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Raison du refus *</label>
                                <Textarea
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    placeholder="Expliquez pourquoi vous refusez cette clôture..."
                                    rows={3}
                                />
                            </div>
                        </>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRejectModal(false)} disabled={processing}>
                            Annuler
                        </Button>
                        <Button 
                            variant="destructive" 
                            onClick={handleReject}
                            disabled={processing || !rejectReason.trim()}
                        >
                            {processing ? 'Refus...' : 'Confirmer le refus'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Recount Request Modal */}
            <Dialog open={showRecountModal} onOpenChange={setShowRecountModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-orange-600">
                            <RotateCcw className="w-5 h-5" />
                            Demander un recomptage
                        </DialogTitle>
                        <DialogDescription>
                            L'employé sera notifié qu'il doit recompter sa caisse.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedClosure && (
                        <>
                            <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                                <p><strong>Caisse:</strong> {selectedClosure.register_name}</p>
                                <p><strong>Par:</strong> {selectedClosure.user_name}</p>
                                <p><strong>Écart actuel:</strong> <VarianceDisplay variance={selectedClosure.variance} /></p>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Instructions (optionnel)</label>
                                <Textarea
                                    value={recountReason}
                                    onChange={(e) => setRecountReason(e.target.value)}
                                    placeholder="Instructions pour le recomptage..."
                                    rows={2}
                                />
                            </div>
                        </>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRecountModal(false)} disabled={processing}>
                            Annuler
                        </Button>
                        <Button 
                            className="bg-orange-600 hover:bg-orange-700" 
                            onClick={handleRequestRecount}
                            disabled={processing}
                        >
                            {processing ? 'Envoi...' : 'Demander recomptage'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Force Close Modal - ADMIN ONLY */}
            <Dialog open={showForceCloseModal} onOpenChange={setShowForceCloseModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-purple-600">
                            <Shield className="w-5 h-5" />
                            Forcer la clôture
                        </DialogTitle>
                        <DialogDescription>
                            <span className="text-red-600 font-semibold">⚠️ Action administrateur</span> - Cette action clôturera de force sans la validation hiérarchique standard.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedClosure && (
                        <>
                            <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg space-y-2 border-2 border-purple-200 dark:border-purple-800">
                                <p className="font-semibold text-gray-900 dark:text-white"><strong>Caisse:</strong> {selectedClosure.register_name}</p>
                                <p className="text-gray-700 dark:text-gray-300"><strong>Par:</strong> {selectedClosure.user_name} ({selectedClosure.user_role})</p>
                                <p className="text-gray-700 dark:text-gray-300"><strong>Écart:</strong> <VarianceDisplay variance={selectedClosure.variance} /></p>
                                <p className="text-gray-700 dark:text-gray-300"><strong>Statut actuel:</strong> {selectedClosure.raw_status || selectedClosure.status}</p>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Raison de la clôture forcée (optionnel)</label>
                                <Textarea
                                    value={forceCloseReason}
                                    onChange={(e) => setForceCloseReason(e.target.value)}
                                    placeholder="Expliquez pourquoi vous forcez cette clôture..."
                                    rows={2}
                                />
                            </div>
                            <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg text-sm text-amber-800 dark:text-amber-300">
                                <strong>⚠️ Cette action sera enregistrée</strong> avec votre nom, la date et l'heure.
                            </div>
                        </>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowForceCloseModal(false)} disabled={processing}>
                            Annuler
                        </Button>
                        <Button 
                            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold" 
                            onClick={handleForceClose}
                            disabled={processing}
                        >
                            <Shield className="w-4 h-4 mr-2" />
                            {processing ? 'Forçage...' : 'Forcer la clôture'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CashClosuresPage;
